// Stato globale della chiamata vocale: montato una volta in App, cosi' la
// chiamata sopravvive alla navigazione. Parla con il nostro backend per il
// token e con l'SDK ElevenLabs (WebRTC) per l'audio.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ConversationProvider, useConversation } from "@elevenlabs/react";
import {
  VOICE_CLIENT_TIME_UP_LEAD_SECONDS,
  VOICE_TIME_UP_SIGNAL,
  type ClientCallStatus,
  type StartVoiceCallResponse,
} from "@shared/voice";
import { CallScreen } from "./CallScreen";
import { Ringback } from "./ringback";
import type { CallErrorCode } from "./call-copy";

export type CallPhase = "idle" | "ringing" | "connecting" | "connected" | "ended";

export interface CallPeer {
  id: string;
  name: string;
  photoUrl?: string | null;
}

interface VoiceCallContextValue {
  phase: CallPhase;
  startCall: (peer: CallPeer) => void;
}

const VoiceCallContext = createContext<VoiceCallContextValue | null>(null);

// Squillo minimo prima che il companion "risponda", per realismo.
const MIN_RING_MS = 2500;
const RING_JITTER_MS = 2500;
// Oltre la fine concessa: se la chiamata e' ancora aperta la chiude il client.
const HARD_STOP_GRACE_SECONDS = 10;
// Chiusura automatica della schermata a fine chiamata senza errori.
const ENDED_AUTO_CLOSE_MS = 2500;

interface ActiveCall extends StartVoiceCallResponse {
  connected: boolean;
  timeUpSent: boolean;
}

// keepalive: la richiesta parte anche se la pagina si sta chiudendo.
function reportStatus(callId: string, status: ClientCallStatus): void {
  fetch(`/api/voice/calls/${encodeURIComponent(callId)}/status`, {
    method: "POST",
    credentials: "include",
    keepalive: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status }),
  }).catch(() => {});
}

function micErrorCode(err: unknown): CallErrorCode {
  const name = (err as any)?.name;
  if (name === "NotAllowedError" || name === "SecurityError") return "mic_denied";
  if (name === "NotFoundError" || name === "OverconstrainedError") return "mic_unavailable";
  return "mic_denied";
}

function VoiceCallController({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<CallPhase>("idle");
  const [peer, setPeer] = useState<CallPeer | null>(null);
  const [error, setError] = useState<CallErrorCode | null>(null);
  const [connectedAt, setConnectedAt] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  const [networkWarning, setNetworkWarning] = useState(false);

  const callRef = useRef<ActiveCall | null>(null);
  const cancelledRef = useRef(false);
  const ringbackRef = useRef(new Ringback());

  const finish = useCallback((code: CallErrorCode | null) => {
    ringbackRef.current.stop();
    const call = callRef.current;
    // Una chiamata connessa va segnalata come chiusa: senza, il server la
    // considera in corso fino al webhook e blocca le chiamate successive.
    if (call) reportStatus(call.callId, call.connected ? "ended" : code ? "failed" : "rejected");
    callRef.current = null;
    setError(code);
    setNetworkWarning(false);
    setPhase("ended");
  }, []);

  const conversation = useConversation({
    onConnect: () => {
      const call = callRef.current;
      if (!call) return;
      call.connected = true;
      reportStatus(call.callId, "active");
      setConnectedAt(Date.now());
      setPhase("connected");
    },
    onDisconnect: (details) => {
      if (!callRef.current) return;
      const interrupted = details.reason === "error";
      finish(interrupted ? (navigator.onLine ? "interrupted" : "offline") : null);
    },
    onError: (message) => {
      console.error("[VoiceCall] errore sessione:", message);
      // Sessione rifiutata prima della connessione (es. limite di chiamate
      // contemporanee): l'SDK chiama solo onError, senza onDisconnect.
      const call = callRef.current;
      if (call && !call.connected) finish("provider_error");
    },
    // Con un errore lato ElevenLabs l'SDK puo' chiudere la sessione senza
    // chiamare onDisconnect: qui la chiamata si chiude comunque.
    onStatusChange: ({ status }) => {
      if (!callRef.current?.connected) return;
      if (status === "disconnected") {
        finish(navigator.onLine ? "interrupted" : "offline");
      }
    },
  });

  // Ref per gli effetti a intervallo: l'oggetto del hook cambia a ogni render.
  const conversationRef = useRef(conversation);
  conversationRef.current = conversation;
  const finishRef = useRef(finish);
  finishRef.current = finish;

  const startCall = useCallback((target: CallPeer) => {
    if (phase !== "idle") return;
    cancelledRef.current = false;
    callRef.current = null;
    setPeer(target);
    setError(null);
    setConnectedAt(null);
    setPhase("ringing");
    const ringStartedAt = Date.now();
    ringbackRef.current.start();

    (async () => {
      // Permesso microfono PRIMA di chiedere il token: niente chiamata sprecata.
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((t) => t.stop());
      } catch (err) {
        return finish(micErrorCode(err));
      }
      if (cancelledRef.current) return;

      let data: StartVoiceCallResponse;
      try {
        const res = await fetch("/api/voice/calls", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ aiProfileId: target.id }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) return finish((body?.error as CallErrorCode) || "provider_error");
        data = body as StartVoiceCallResponse;
      } catch {
        return finish(navigator.onLine ? "provider_error" : "offline");
      }

      callRef.current = { ...data, connected: false, timeUpSent: false };
      const ringFor = MIN_RING_MS + Math.random() * RING_JITTER_MS - (Date.now() - ringStartedAt);
      if (ringFor > 0) await new Promise((r) => setTimeout(r, ringFor));
      if (cancelledRef.current) {
        reportStatus(data.callId, "rejected");
        callRef.current = null;
        return;
      }

      ringbackRef.current.stop();
      setPhase("connecting");
      conversation.startSession({
        conversationToken: data.conversationToken,
        connectionType: "webrtc",
        overrides: {
          agent: { firstMessage: data.firstMessage },
          tts: { voiceId: data.voiceId },
        },
        // Arriva al nostro Custom LLM come elevenlabs_extra_body.call_token
        // (gli header personalizzati della dashboard non vengono inviati).
        customLlmExtraBody: { call_token: data.callToken },
      });
    })();
  }, [phase, finish, conversation]);

  const hangUp = useCallback(() => {
    if (phase === "ringing") {
      cancelledRef.current = true;
      finish(null);
      return;
    }
    // Chiudiamo subito noi (stato + segnalazione al server) senza aspettare
    // onDisconnect, che dopo finish() trova callRef vuoto e non fa nulla.
    try {
      conversation.endSession();
    } catch {
      // Sessione gia' chiusa dall'SDK (es. dopo un errore del server).
    }
    finish(null);
  }, [phase, finish, conversation]);

  const close = useCallback(() => {
    setPhase("idle");
    setPeer(null);
    setError(null);
    conversation.setMuted(false);
  }, [conversation]);

  // Timer, segnale di fine tempo al Custom LLM, chiusura di riserva.
  useEffect(() => {
    if (phase !== "connected") return;
    const id = setInterval(() => {
      const t = Date.now();
      setNow(t);
      const call = callRef.current;
      if (!call || connectedAt === null) return;
      const remaining = call.allowedSeconds - (t - connectedAt) / 1000;
      if (remaining <= VOICE_CLIENT_TIME_UP_LEAD_SECONDS && !call.timeUpSent) {
        call.timeUpSent = true;
        conversationRef.current.sendUserMessage(VOICE_TIME_UP_SIGNAL);
      }
      if (remaining <= -HARD_STOP_GRACE_SECONDS) {
        try {
          conversationRef.current.endSession();
        } catch {
          // gia' chiusa
        }
        finishRef.current(null);
      }
    }, 1000);
    return () => clearInterval(id);
  }, [phase, connectedAt]);

  // Scheda chiusa o ricaricata a chiamata aperta.
  useEffect(() => {
    if (phase !== "connected") return;
    const onPageHide = () => {
      const call = callRef.current;
      if (call?.connected) reportStatus(call.callId, "ended");
    };
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, [phase]);

  // Rete: avviso durante la chiamata (la disconnessione vera arriva dall'SDK).
  useEffect(() => {
    if (phase !== "connected") return;
    const off = () => setNetworkWarning(true);
    const on = () => setNetworkWarning(false);
    window.addEventListener("offline", off);
    window.addEventListener("online", on);
    return () => {
      window.removeEventListener("offline", off);
      window.removeEventListener("online", on);
    };
  }, [phase]);

  useEffect(() => {
    if (phase !== "ended" || error) return;
    const id = setTimeout(close, ENDED_AUTO_CLOSE_MS);
    return () => clearTimeout(id);
  }, [phase, error, close]);

  useEffect(() => () => ringbackRef.current.stop(), []);

  const value = useMemo(() => ({ phase, startCall }), [phase, startCall]);

  return (
    <VoiceCallContext.Provider value={value}>
      {children}
      {phase !== "idle" && peer && (
        <CallScreen
          phase={phase}
          peer={peer}
          elapsedSeconds={connectedAt ? (now - connectedAt) / 1000 : 0}
          isMuted={conversation.isMuted}
          isSpeaking={conversation.isSpeaking}
          networkWarning={networkWarning}
          error={error}
          onToggleMute={() => conversation.setMuted(!conversation.isMuted)}
          onHangUp={hangUp}
          onClose={close}
        />
      )}
    </VoiceCallContext.Provider>
  );
}

export function VoiceCallProvider({ children }: { children: ReactNode }) {
  return (
    <ConversationProvider>
      <VoiceCallController>{children}</VoiceCallController>
    </ConversationProvider>
  );
}

export function useVoiceCall(): VoiceCallContextValue {
  const ctx = useContext(VoiceCallContext);
  if (!ctx) throw new Error("useVoiceCall va usato dentro VoiceCallProvider");
  return ctx;
}
